import api from './api';
import { isExamDurationId } from '../utils/examSubscriptionIds';

const id = (value) => Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : 0;

function unwrap(response, fallback) {
  const body = response?.data || {};
  if (body.error || (body.code != null && Number(body.code) !== 200)) {
    const error = new Error(body.message || fallback);
    error.backend = body;
    throw error;
  }
  if (body.dataset?.data == null) throw new Error(body.message || fallback);
  return body.dataset.data;
}

export async function fetchExamPlans(stateId = 0) {
  const data = unwrap(await api.get('/exam-courses/exam-plans', { params: { states_id: id(stateId) } }), 'Unable to load exam plans.');
  return { ...data, exam_plans: Array.isArray(data.exam_plans) ? data.exam_plans : [] };
}

export async function fetchUserExamPlans() {
  let response;
  try {
    response = await api.get('/exam-courses/get-user-exam-plans');
  } catch (error) {
    if (/no active exam plans found/i.test(error.message || '')) return [];
    throw error;
  }
  if (/no active exam plans found/i.test(response?.data?.message || '')) return [];
  const data = unwrap(response, 'Unable to load your exam subscriptions.');
  if (!Array.isArray(data)) throw new Error('The server returned invalid purchased exam plans.');
  return data;
}

export async function fetchExamPlanDetail({ stateId = 0, planId, durationId }, generateOrder = false) {
  const oneTimeDuration = durationId === null || durationId === 0 || durationId === '0';
  if (!id(planId) || (!oneTimeDuration && !isExamDurationId(durationId))) throw new Error('Select a valid exam plan and duration.');
  const detail = unwrap(await api.get('/exam-courses/exam-plan-detail', {
    // Keep the same request headers as the working detail read. Adding a
    // Cache-Control header changes the browser preflight requirements.
    // Axios omits null query parameters; the backend reads an omitted duration as null.
    params: { states_id: id(stateId), exam_plan_id: id(planId), duration_id: oneTimeDuration ? null : Number(durationId), is_order_generate: generateOrder ? 1 : 0, ...(generateOrder ? { _: Date.now() } : {}) },
  }), generateOrder ? 'Unable to create exam subscription order.' : 'Unable to load exam plan details.');
  if (generateOrder && (!String(detail.vk_order_id || '').trim() || !id(detail.plan_id))) throw new Error('The server did not return a valid exam order and plan ID.');
  return detail;
}

export function activeExamPrice(detail, durationId) {
  // The backend represents one-time durations as null; existing selections may use 0.
  const price = (Array.isArray(detail?.subscription_price) ? detail.subscription_price : []).find(row => [true, 1, '1', 'true'].includes(row.active) && (durationId == null || (row.duration_id === null && Number(durationId) === 0) || (isExamDurationId(row.duration_id) && Number(row.duration_id) === Number(durationId))));
  if (!price || (price.duration_id !== null && !isExamDurationId(price.duration_id)) || !Number.isFinite(Number(price.value)) || Number(price.value) <= 0) return null;
  return price;
}

export async function fetchExamGateways(planId, couponCode = '') {
  if (!id(planId)) throw new Error('The exam order is missing its payment plan ID.');
  // Use this application's existing browser query transport. The backend must
  // support these parameters; Flutter's GET JSON body cannot be sent by browsers.
  const data = unwrap(await api.get('/payment/get-payment-gateway', { params: {
    platform: process.env.REACT_APP_EXAM_PAYMENT_PLATFORM || 'web',
    exam_plan_id: id(planId),
    ...(couponCode ? { coupon_code: couponCode } : {}),
  } }), 'Unable to load exam payment methods.');
  if (!Array.isArray(data)) throw new Error('The server returned invalid payment methods.');
  return data.map(({ client_secret, ...publicGateway }) => publicGateway);
}

export { createCashfreeOrder as createCashfreeExamOrder } from './cashfreeService';
